// src/utils/costCalculationUtils.js

/**
 * Cost Calculation Utilities for Production Management
 */

/**
 * Calculate detailed production cost breakdown
 * @param {Object} recipe - Production recipe
 * @param {Array} rawMaterials - Available raw materials with prices
 * @param {Array} subcontractors - Available subcontractors with pricing
 * @param {Object} laborSettings - Labor cost settings
 * @param {Object} overheadSettings - Overhead cost settings
 * @returns {Object} Detailed cost breakdown
 */
export const calculateDetailedCost = (recipe, rawMaterials, subcontractors, laborSettings, overheadSettings) => {
    const costBreakdown = {
        rawMaterials: [],
        subcontracting: [],
        labor: [],
        overhead: []
    };

    let totalRawMaterialCost = 0;
    let totalSubcontractingCost = 0;
    let totalLaborCost = 0;
    let totalOverheadCost = 0;

    // Calculate raw material costs
    recipe.stages.forEach(stage => {
        stage.inputMaterials.forEach(material => {
            const rawMaterial = rawMaterials.find(rm => rm.name === material.material);
            if (rawMaterial) {
                const materialCost = (rawMaterial.unitPrice || 0) * material.quantity;
                totalRawMaterialCost += materialCost;

                costBreakdown.rawMaterials.push({
                    id: `${stage.id}-${material.material}`,
                    stageName: stage.name,
                    materialName: material.material,
                    quantity: material.quantity,
                    unit: material.unit,
                    unitPrice: rawMaterial.unitPrice || 0,
                    total: parseFloat(materialCost.toFixed(2))
                });
            }
        });
    });

    // Calculate subcontracting costs
    recipe.stages.forEach(stage => {
        if (stage.type === 'subcontracting' && stage.subcontractor) {
            const subcontractor = subcontractors.find(s => s.id === stage.subcontractor.supplierId);
            if (subcontractor && subcontractor.costTariff) {
                // Find matching cost tariff
                const costItem = subcontractor.costTariff.find(item => {
                    if (item.type === 'per_unit') return item.unit === stage.outputProduct.unit;
                    if (item.type === 'per_kg') return item.unit === 'Kg';
                    return item.type === 'per_job';
                });

                if (costItem) {
                    let subcontractingCost = 0;
                    if (costItem.type === 'per_unit') {
                        subcontractingCost = costItem.value * stage.outputProduct.quantity;
                    } else if (costItem.type === 'per_kg') {
                        // Assuming we have weight information
                        subcontractingCost = costItem.value * (stage.outputProduct.weight || 1);
                    } else {
                        subcontractingCost = costItem.value;
                    }

                    totalSubcontractingCost += subcontractingCost;

                    costBreakdown.subcontracting.push({
                        id: `${stage.id}-subcontracting`,
                        stageName: stage.name,
                        subcontractorName: subcontractor.name,
                        serviceType: costItem.type,
                        quantity: stage.outputProduct.quantity,
                        unit: stage.outputProduct.unit,
                        unitPrice: costItem.value,
                        total: parseFloat(subcontractingCost.toFixed(2))
                    });
                }
            }
        }
    });

    // Calculate labor costs
    recipe.stages.forEach(stage => {
        // Labor rate from settings or default to 15 TRY/hour
        const laborRate = laborSettings.hourlyRate || 15;
        // Duration in hours (assuming duration is in minutes)
        const laborHours = (stage.duration || 0) / 60;
        const laborCost = laborHours * laborRate;
        totalLaborCost += laborCost;

        if (laborCost > 0) {
            costBreakdown.labor.push({
                id: `${stage.id}-labor`,
                stageName: stage.name,
                hours: parseFloat(laborHours.toFixed(2)),
                hourlyRate: laborRate,
                total: parseFloat(laborCost.toFixed(2))
            });
        }
    });

    // Calculate overhead costs
    // Base overhead on raw materials and labor
    const overheadBase = totalRawMaterialCost + totalLaborCost;

    if (overheadSettings.type === 'percentage') {
        // Percentage-based overhead
        const overheadPercentage = overheadSettings.rate || 10;
        totalOverheadCost = overheadBase * (overheadPercentage / 100);

        costBreakdown.overhead.push({
            id: 'overhead-percentage',
            description: `Genel gider (${overheadPercentage}%)`,
            rate: `${overheadPercentage}%`,
            baseAmount: parseFloat(overheadBase.toFixed(2)),
            total: parseFloat(totalOverheadCost.toFixed(2))
        });
    } else if (overheadSettings.type === 'fixed') {
        // Fixed overhead per unit
        const fixedOverheadPerUnit = overheadSettings.rate || 5;
        totalOverheadCost = fixedOverheadPerUnit * recipe.stages.reduce((sum, stage) =>
            sum + (stage.outputProduct?.quantity || 0), 0);

        costBreakdown.overhead.push({
            id: 'overhead-fixed',
            description: `Sabit genel gider`,
            rate: `${fixedOverheadPerUnit} ₺/birim`,
            baseAmount: recipe.stages.reduce((sum, stage) =>
                sum + (stage.outputProduct?.quantity || 0), 0),
            total: parseFloat(totalOverheadCost.toFixed(2))
        });
    }

    return {
        breakdown: costBreakdown,
        totals: {
            rawMaterials: parseFloat(totalRawMaterialCost.toFixed(2)),
            subcontracting: parseFloat(totalSubcontractingCost.toFixed(2)),
            labor: parseFloat(totalLaborCost.toFixed(2)),
            overhead: parseFloat(totalOverheadCost.toFixed(2)),
            total: parseFloat((totalRawMaterialCost + totalSubcontractingCost + totalLaborCost + totalOverheadCost).toFixed(2))
        }
    };
};

/**
 * Calculate unit cost for a product
 * @param {number} totalCost - Total production cost
 * @param {number} quantity - Production quantity
 * @returns {number} Unit cost
 */
export const calculateUnitCost = (totalCost, quantity) => {
    if (quantity <= 0) return 0;
    return parseFloat((totalCost / quantity).toFixed(2));
};

/**
 * Calculate profit margin
 * @param {number} unitCost - Unit production cost
 * @param {number} salePrice - Sale price
 * @returns {Object} Profit margin information
 */
export const calculateProfitMargin = (unitCost, salePrice) => {
    if (salePrice <= 0) return { margin: 0, percentage: 0 };

    const profit = salePrice - unitCost;
    const marginPercentage = (profit / salePrice) * 100;

    return {
        profit: parseFloat(profit.toFixed(2)),
        percentage: parseFloat(marginPercentage.toFixed(2))
    };
};

/**
 * Generate cost comparison report
 * @param {Array} products - Products with cost information
 * @returns {Array} Comparison data
 */
export const generateCostComparison = (products) => {
    return products.map(product => ({
        id: product.id,
        name: product.name,
        unitCost: product.unitCost?.total || 0,
        salePrice: product.salePrice || 0,
        profitMargin: product.profitMargin?.percentage || 0,
        category: product.category || 'Genel'
    }));
};

/**
 * Calculate cost trends
 * @param {Array} historicalCosts - Historical cost data
 * @returns {Object} Trend analysis
 */
export const calculateCostTrends = (historicalCosts) => {
    if (historicalCosts.length < 2) {
        return { trend: 'stable', percentage: 0 };
    }

    const firstCost = historicalCosts[0].totalCost;
    const lastCost = historicalCosts[historicalCosts.length - 1].totalCost;

    if (firstCost === 0) {
        return { trend: 'stable', percentage: 0 };
    }

    const percentageChange = ((lastCost - firstCost) / firstCost) * 100;

    let trend = 'stable';
    if (percentageChange > 5) trend = 'increasing';
    else if (percentageChange < -5) trend = 'decreasing';

    return {
        trend,
        percentage: parseFloat(percentageChange.toFixed(2))
    };
};

/**
 * Generate cost breakdown for reporting
 * @param {Object} costData - Detailed cost data
 * @returns {Array} Formatted breakdown for reports
 */
export const generateReportBreakdown = (costData) => {
    const breakdown = [];

    // Raw materials
    breakdown.push({
        category: 'Hammadde Maliyeti',
        items: costData.breakdown.rawMaterials.map(item => ({
            description: `${item.materialName} (${item.stageName})`,
            quantity: `${item.quantity} ${item.unit}`,
            unitPrice: item.unitPrice,
            total: item.total
        })),
        total: costData.totals.rawMaterials
    });

    // Subcontracting
    breakdown.push({
        category: 'Fason Maliyeti',
        items: costData.breakdown.subcontracting.map(item => ({
            description: `${item.subcontractorName} (${item.stageName})`,
            quantity: `${item.quantity} ${item.unit}`,
            unitPrice: item.unitPrice,
            total: item.total
        })),
        total: costData.totals.subcontracting
    });

    // Labor
    breakdown.push({
        category: 'İşçilik Maliyeti',
        items: costData.breakdown.labor.map(item => ({
            description: item.stageName,
            quantity: `${item.hours} saat`,
            unitPrice: item.hourlyRate,
            total: item.total
        })),
        total: costData.totals.labor
    });

    // Overhead
    breakdown.push({
        category: 'Genel Giderler',
        items: costData.breakdown.overhead.map(item => ({
            description: item.description,
            quantity: item.baseAmount,
            unitPrice: item.rate,
            total: item.total
        })),
        total: costData.totals.overhead
    });

    return breakdown;
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currency
    }).format(amount);
};

const costCalculationUtils = {
    calculateDetailedCost,
    calculateUnitCost,
    calculateProfitMargin,
    generateCostComparison,
    calculateCostTrends,
    generateReportBreakdown,
    formatCurrency
};

export default costCalculationUtils;
